import { Radio } from '@atgfr8/atg-ui-tailwind';
import { For, type JSX } from 'solid-js';

import './RadioGroup.css';

export type RadioOptionValue = string | number | boolean;

export type RadioOptionT<T extends RadioOptionValue = string> = {
  label: string | JSX.Element;
  value: T;
  disabled?: boolean;
};

export type RadioGroupProps<T extends RadioOptionValue = string> = {
  options: RadioOptionT<T>[];
  value?: T;
  onChange?: (value: T) => void;
  label?: string;
  labelClass?: string;
  direction?: 'horizontal' | 'vertical';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  color?: 'primary' | 'info' | 'success' | 'warning' | 'error';
  disabled?: boolean;
  error?: string | string[] | null;
  class?: string;
  optionClass?: string;
  required?: boolean;
};

/**
 * Generic over the value type — supports string (default), number, or boolean
 * options without needing to stringify on the way in and parse on the way out.
 *
 * @example
 * // Number values:
 * <RadioGroup
 *   value={mode()}              // number
 *   onChange={setMode}          // (n: number) => void
 *   options={[
 *     { label: 'Block', value: 1 },
 *     { label: 'Include', value: 2 },
 *   ]}
 * />
 *
 * @example
 * // Vertical:
 * <RadioGroup
 *   label="Bid Type"
 *   name="bid-type"
 *   direction="vertical"
 *   value={value()}
 *   onChange={setValue}
 *   options={[
 *     { label: 'Spot', value: 'spot' },
 *     { label: 'Contract', value: 'contract' },
 *   ]}
 * />
 *
 * @example
 * // Horizontal with inline label:
 * <RadioGroup
 *   label="Group by:"
 *   labelClass="font-bold whitespace-nowrap"
 *   class="flex-row! items-center gap-[10px]"
 *   direction="horizontal"
 *   ...
 * />
 *
 * @example
 * // Per-option card styling:
 * <RadioGroup
 *   direction="vertical"
 *   optionClass="w-full! bg-[#E8F0F6]! rounded-[5px]! py-[10px] px-[17px]"
 *   ...
 * />
 */
export const RadioGroup = <T extends RadioOptionValue = string>(
  props: RadioGroupProps<T>,
) => {
  const direction = () => props.direction ?? 'horizontal';
  const size = () => props.size ?? 'sm';
  const color = () => props.color ?? 'primary';

  const errorMsg = () => {
    if (!props.error) return null;
    return Array.isArray(props.error) ? (props.error[0] ?? null) : props.error;
  };

  const optionClass = () => {
    const compact = direction() === 'vertical' ? '[&>div]:p-[5px]' : '';
    return `${compact} ${props.optionClass ?? ''}`.trim();
  };

  return (
    <div
      class={`radio-group-container ${props.class ?? ''}`}
      role="radiogroup"
      aria-label={props.label || 'Select an option'}
    >
      {props.label && (
        <span
          class={`radio-group-label roboto ${direction() === 'vertical' ? 'mb-1' : ''} ${props.labelClass ?? ''}`}
        >
          {props.label}
          {props.required && <span class="radio-group-required"> *</span>}
        </span>
      )}
      <div
        class={`flex flex-wrap gap-2 ${direction() === 'vertical' ? 'flex-col' : 'flex-row'}`}
      >
        <For each={props.options}>
          {(option) => (
            <Radio
              label={option.label}
              size={size()}
              color={color()}
              checked={props.value === option.value}
              onChange={() => props.onChange?.(option.value)}
              disabled={option.disabled ?? props.disabled}
              class={optionClass()}
            />
          )}
        </For>
      </div>
      {errorMsg() && <span class="radio-group-error">{errorMsg()}</span>}
    </div>
  );
};
